import * as React from 'react';
import { IEveProductDimension, IWebGroupHeaders, IWebGroupProductData } from '../../../actions/DataServiceEntities.g';
import { PriceInfo } from './price-info';
import { ICallerContext } from '@msdyn365-commerce/retail-proxy';
import { QuantityError } from './quantity-error';
import Input from 'reactstrap/lib/Input';
import { IWebGroupCartLine } from '../context/webgroup-cart-context';

export interface IWebGroupLineProps {
    product: IWebGroupProductData;
    actionContext: ICallerContext;
    updateLine: (line: IWebGroupCartLine) => void;
    isAuthenticated: boolean;
    searchTerm: string;
    bigMode: boolean;
    headers: IWebGroupHeaders;
    forcePropColumns: number;
}

export const WebGroupLine: React.FC<IWebGroupLineProps> = (props: IWebGroupLineProps) => {
    const [selectedVariant, setSelectedVariant] = React.useState<IEveProductDimension>();
    //const [currentQtyStep, setCurrentQtyStep] = React.useState<number>(0);
    const [purchaseQty, setPurchaseQty] = React.useState<number>(0);

    const captionValues = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

    React.useEffect(() => {
        if (props.product.Dimensions?.length && props.product.Dimensions.length > 0 && selectedVariant === undefined) {
            setSelectedVariant(props.product.Dimensions[0]);
            //setCurrentQtyStep(props.product.Dimensions[0].QtyPerConfig);
            setPurchaseQty(0);
        } else if (props.product.ProductValues && selectedVariant) {
            //If we have changed variant or purchase qty, we need to update the line in the webgroupgroup.
            props.updateLine({
                product: props.product.ProductValues,
                variant: selectedVariant,
                quantity: purchaseQty,
                error: isQtyError()
            });
        }
    }, [purchaseQty, selectedVariant]);

    const setQuantity = (value: number) => {
        setPurchaseQty(value);
    };

    const readProp = (obj: any, prop: string): string => {
        return obj[`${prop}`];
    };

    const renderProductSpecs = (isBigMode: boolean = false): JSX.Element[] | null => {
        // Process captions and values and combine for iteration
        let specs = [];
        for (const item of captionValues) {
            const key = `Value${item}`;
            const value = readProp(props.product.ProductValues, key);
            if (value !== '') {
                specs.push({
                    key,
                    value: readProp(props.product.ProductValues, key)
                });
            }
        }
        while (specs.length < props.forcePropColumns) {
            specs.push({
                key: `empty-${specs.length}`,
                value: ''
            });
        }
        if (specs.length > props.forcePropColumns) {
            specs = specs.slice(0, props.forcePropColumns);
        }

        if (isBigMode) {
            return specs?.map(s => {
                const captionkey = s.key.replace('Value', 'Caption');
                return (
                    <div className='product-specifications' key={s.key}>
                        {/* @ts-ignore */}
                        <span className='spec-title'>{props.headers[captionkey]}:</span> {s.value}
                    </div>
                );
            });
        } else if (!props.product.ProductValues) {
            return specs?.map(s => {
                return (
                    <div className='product-specifications' key={s.key}>
                        {s.value}
                    </div>
                );
            });
        } else {
            return specs?.map(s => {
                return <td key={s.key}>{s.value}</td>;
            });
        }
    };

    const renderDimensions = (d: IEveProductDimension | undefined): string => {
        return `${d?.QtyPerConfig} / ${d?.Config} (${d?.Color})`; //d.Color && d.Color !== '.' ?
    };

    const renderProductDimensions = (): JSX.Element[] | undefined => {
        if (props.product.Dimensions) {
            props.product.Dimensions = props.product.Dimensions.filter(
                (obj, index, self) => index === self.findIndex(t => t.InventDimId === obj.InventDimId)
            );
        }
        return props.product.Dimensions?.map(d => {
            return (
                <option key={d.ProductId} value={d.ProductId}>
                    {renderDimensions(d)}
                </option>
            );
        });
    };

    const onChangeVariant = (e: number) => {
        const newVariant: IEveProductDimension | undefined = props.product.Dimensions?.find(i => i.ProductId === e);
        if (newVariant) {
            setSelectedVariant(newVariant);
        }
    };

    const isQtyError = (): boolean => {
        return purchaseQty % (selectedVariant?.QtyPerConfig ?? 0) > 0;
    };

    const renderRegularLine = (): JSX.Element => {
        return (
            <tr key={props.product.ProductValues?.ItemId}>
                <td className={props.searchTerm === props.product.ProductValues?.ItemId ? 'item-row odd highlight' : 'item-row odd'}>
                    {props.product.ProductValues?.ItemId}
                </td>
                {renderProductSpecs()}
                <td>
                    {props.product.Dimensions && props.product.Dimensions.length > 1 ? (
                        <select className='variant-selector' onChange={e => onChangeVariant(parseInt(e.currentTarget.value, 10))}>
                            {renderProductDimensions()}
                        </select>
                    ) : (
                        renderDimensions(props.product.Dimensions?.find(i => i.ProductId === selectedVariant?.ProductId))
                    )}
                </td>
                <td>
                    {props.isAuthenticated ? (
                        <PriceInfo
                            itemId={props.product.ProductValues?.ItemId || ''}
                            context={props.actionContext}
                            product={props.product.ProductValues!}
                            selectedVariant={selectedVariant?.ProductId ?? 0}
                        ></PriceInfo>
                    ) : (
                        <>Sign in to view Prices</>
                    )}
                </td>
                <td>
                    {props.isAuthenticated ? (
                        <div className='quantity-wrapper single'>
                            <Input
                                type='number'
                                className='product-qty'
                                step={selectedVariant?.QtyPerConfig}
                                min={0}
                                pattern='[0-9]+'
                                onChange={e => setQuantity(parseInt(e.currentTarget.value, 10))}
                                value={purchaseQty}
                                defaultValue={'0'}
                                onKeyPress={e => {
                                    if (['-', 'e'].includes(e.key.toLocaleLowerCase())) {
                                        e.preventDefault();
                                        return;
                                    }
                                }}
                            />
                            <QuantityError qtyPerConfig={selectedVariant?.QtyPerConfig ?? 0} show={isQtyError()} />
                        </div>
                    ) : (
                        <></>
                    )}
                </td>
            </tr>
        );
    };

    const renderBigMode = (): JSX.Element => {
        const product = props.product;
        return (
            <>
                <h5>Configuration (Qty) / Source</h5>
                <p>
                    {props.product.Dimensions && props.product.Dimensions.length > 1 ? (
                        <select className='variant-selector' onChange={e => onChangeVariant(parseInt(e.currentTarget.value, 10))}>
                            {renderProductDimensions()}
                        </select>
                    ) : (
                        renderDimensions(props.product.Dimensions?.find(i => i.ProductId === selectedVariant?.ProductId))
                    )}
                </p>
                {props.isAuthenticated ? (
                    <PriceInfo
                        itemId={props.product.ProductValues?.ItemId || ''}
                        context={props.actionContext}
                        product={props.product.ProductValues!}
                        selectedVariant={selectedVariant?.ProductId ?? 0}
                        singleProduct={props.bigMode}
                    ></PriceInfo>
                ) : (
                    <p>Sign in to view Prices</p>
                )}
                {props.isAuthenticated ?? <strong>Quantity:</strong>}
                {/* <input type='number' className='product-qty' step={1} min={0} onChange={e => updateLine(e, firstProduct)} /> */}
                {props.isAuthenticated ? (
                    <div className='quantity-wrapper single'>
                        <Input
                            type='number'
                            className='product-qty'
                            step={selectedVariant?.QtyPerConfig}
                            min={0}
                            pattern='[0-9]+'
                            onChange={e => setQuantity(parseInt(e.currentTarget.value, 10))}
                            value={purchaseQty}
                            defaultValue={'0'}
                            onKeyPress={e => {
                                if (['-', 'e'].includes(e.key.toLocaleLowerCase())) {
                                    e.preventDefault();
                                    return;
                                }
                            }}
                        />
                        <QuantityError qtyPerConfig={selectedVariant?.QtyPerConfig ?? 0} show={isQtyError()} />
                    </div>
                ) : (
                    <></>
                )}
                <h5>Specifications</h5>
                {product.ProductValues && renderProductSpecs(true)}
            </>
        );
    };

    if (props.bigMode) {
        return renderBigMode();
    } else {
        return renderRegularLine();
    }
};
